import { Stack, Text } from '@fluentui/react';
import React, { useCallback, useMemo } from 'react';
import downloadIcon from '../../../assets/icons/svg/download.svg';
import eyeIcon from '../../../assets/icons/svg/eye2.svg';
import { useDocumentsManager } from '../../../contexts/documentContext';
import { ResponsiveSize, useResponsiveSize } from '../../../contexts/responsiveContext';
import { saveToComputer } from '../../../helpers';
import SvgIcon from '../../SvgIcon';

const TableDocuments = (props) => {

    const {
        documents = [],
        onPreview = () => null,
    } = props;

    const { getFileData, markAsRead } = useDocumentsManager();
    const { responsiveSize } = useResponsiveSize();

    const styles = useMemo(
        () => {
            const cell = { minWidth: 240, maxWidth: 240, wordBreak: "break-word" };
            const cellSm = { minWidth: 120, maxWidth: 120, wordBreak: "break-word" };

            let styles = {
                // Fluent UI
                noResult: {
                    root: {
                        fontSize: 16,
                        fontWeight: 700,
                        lineHeight: "19px",
                        marginBottom: 12,
                        marginTop: 26,
                    }
                },
                // Not Fluent UI
                body: {
                    borderBottom: "2px solid #D9D9D9",
                    borderTop: "2px solid #D9D9D9"
                },
                cell,
                cellCenter: { ...cell, textAlign: "center" },
                cellRight: { ...cell, textAlign: "right" },
                cellSm,
                cellSmCenter: { ...cellSm, textAlign: "center" },
                cellSmRight: { ...cellSm, textAlign: "right" },
                header: {
                    background: "white",
                    color: "red",
                    fontSize: 12,
                    height: 50,
                    lineHeight: "14px",
                    textAlign: "left",
                    textTransform: "uppercase",
                    verticalAlign: "bottom"
                },
                row: {
                    color: "#8A8A8A",
                    fontSize: 16,
                    fontWeight: 400,
                    height: 50,
                    lineHeight: "19px"
                },
                outer: { display: "grid" },
                table: {
                    borderCollapse: "collapse",
                    display: "block",
                    marginRight: 1,
                    overflowX: "auto"
                }
            };

            return styles;
        },
        []
    );

    const onSave = useCallback(
        async (document) => {
            const fileData = await getFileData(document);
            markAsRead(document);
            saveToComputer(fileData, document.nom);
        },
        [getFileData, markAsRead]
    );

    return Array.isArray(documents) && documents.length > 0
        ? <div style={styles.outer} >
            <table style={styles.table}>
                <thead style={styles.header}>
                    <tr>
                        {responsiveSize === ResponsiveSize.small && <th style={styles.cellSm} />}
                        <th style={{ ...styles.cell, paddingBottom: 10 }}>nom</th>
                        <th style={{ ...styles.cell, paddingBottom: 10 }}>catégorie</th>
                        <th style={{ ...styles.cellSm, paddingBottom: 10 }}>date</th>
                        {responsiveSize >= ResponsiveSize.medium && <th style={styles.cellSm} />}
                    </tr>
                </thead>
                <tbody style={styles.body}>
                    {documents.map((document, index) => {
                        const { creerLe, lu, nom, typeDocument } = document;

                        let trStyle = { ...styles.row };

                        if (index % 2 === 1) trStyle.background = "#F6F6F6";
                        else trStyle.background = "white";

                        if (!lu) trStyle.fontWeight = 700;

                        // Sur petit écran, afficher les icône en début de ligne + inverser les boutons
                        const actions = <td style={{ ...styles.cellSm, padding: 13 }}>
                            <Stack horizontal horizontalAlign="space-between" reversed={responsiveSize === ResponsiveSize.small}>
                                <SvgIcon
                                    disabled={!["pdf", "png", "jpg", "jpeg"].includes(nom.split(".").pop().toLowerCase())}
                                    onClick={event => onPreview(document)}
                                    src={eyeIcon}
                                    title="Prévisualiser"
                                />
                                <SvgIcon
                                    onClick={event => onSave(document)}
                                    src={downloadIcon}
                                    title="Télécharger"
                                />
                            </Stack>
                        </td>;

                        return <tr key={index} style={trStyle}>
                            {responsiveSize === ResponsiveSize.small && actions}
                            <td style={styles.cell}>{nom}</td>
                            <td style={styles.cell}>{typeDocument}</td>
                            <td style={styles.cellSm}>{!!creerLe && new Date(creerLe).toLocaleDateString("fr-FR")}</td>
                            {responsiveSize >= ResponsiveSize.medium && actions}
                        </tr>;
                    })}
                </tbody>
            </table>
        </div>
        : <Text styles={styles.noResult}>Aucun document disponible</Text>;

};

export default TableDocuments;